import React, { useState, useEffect } from "react";
import { Avatar, Button, Drawer, Box, Modal, Badge } from "@mui/material";
import { styled } from "@mui/material/styles";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import GetUserPrefecture from "./GetUserPrefecture";
import { useStreamChatClient } from "../context/StreamChatContext";

/* ───────── ミュート判定ユーティリティ ───────── */
const isChannelMuted = (client, channel) =>
  !!client?.mutedChannels?.some((m) => (m.channel?.cid ?? m.cid) === channel?.cid);

const GroupChatSetting = ({ group, user }) => {
  const chatClient = useStreamChatClient();
  const [channel, setChannel] = useState(null);

  /* ───────── 初期化 ───────── */
  useEffect(() => {
    let mounted = true;
    const initChat = async () => {
      if (!chatClient || !group?.id) return;
      try {
        const ch = chatClient.channel("messaging", "groupChat-" + group.id, {});
        if (mounted) setChannel(ch);
        await ch.watch();
      } catch (e) {
        console.error("channel.watch failed:", e);
      }
    };
    initChat();
    return () => {
      mounted = false;
      // ch?.stopWatching?.().catch(()=>{});
    };
  }, [chatClient, group?.id]);

  /* ───────── メンバー取得 ───────── */
  const [channelMember, setChannelMember] = useState([]);
  useEffect(() => {
    const getChannelMembers = async () => {
      try {
        const sort = { created_at: 1 };
        const res = await channel.queryMembers({}, sort, {});
        setChannelMember(res?.members ?? []);
      } catch (e) {
        console.error("queryMembers failed:", e);
      }
    };
    if (channel) getChannelMembers();
  }, [channel]);

  /* ───────── ミュート制御 ───────── */
  const [isMute, setIsMute] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 初期値＋イベントで追従
  useEffect(() => {
    if (!chatClient || !channel) return;

    const refresh = () => setIsMute(isChannelMuted(chatClient, channel));
    refresh();

    const sub = chatClient.on((e) => {
      if (
        e.type === "channel.muted" ||
        e.type === "channel.unmuted" ||
        e.type === "notification.channel_mutes_updated" ||
        e.type === "notification.mutes_updated" ||
        e.type === "health.check"
      ) {
        refresh();
      }
    });

    return () => sub.unsubscribe();
  }, [chatClient, channel?.cid]);

  const handleMute = async () => {
    if (!channel) return;
    try {
      setIsLoading(true);
      if (isChannelMuted(chatClient, channel)) {
        await channel.unmute();
      } else {
        await channel.mute();
      }
      // 即時反映
      setIsMute(isChannelMuted(chatClient, channel));
    } catch (e) {
      console.error("mute/unmute failed:", e);
    } finally {
      setIsLoading(false);
      handleClose(); // モーダル閉じる
    }
  };

  /* ───────── Mute モーダル ───────── */
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  const MuteComponent = () => (
    <Box sx={style}>
      <div className="rounded-md p-2 border text-center text-white bg-black ">
        チャンネルミュート
      </div>
      <div className="mt-2">このチャンネルのミュート設定を変更しますか？</div>
      <div className="flex mt-3">
        現在の設定：
        {isMute ? (
          <div className="text-red-500">オン</div>
        ) : (
          <div className="text-blue-500">オフ</div>
        )}
      </div>
      <div className="text-xs text-gray-500 mt-3">
        ミュート設定がオンになっていると、このチャンネルが更新されたときの通知が来なくなります
      </div>
      <div className="flex justify-around mt-2">
        <Button
          variant="outlined"
          onClick={handleMute}
          color={!isMute ? "error" : "primary"}
          disabled={isLoading || !channel}
        >
          {isMute ? "オフ" : "オン"}
        </Button>
        <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
          キャンセル
        </Button>
      </div>
    </Box>
  );

  /* ───────── ユーザーがVillagerか ───────── */
  const isVillager = async (userId) => {
    try {
      const snap = await getDoc(doc(db, "users", userId));
      return snap.exists() && snap.data().isVillager === true;
    } catch (error) {
      console.error("Error getting document:", error);
      return false;
    }
  };

  /* ───────── Villager付きメンバー配列 ───────── */
  const [channelMemberWithVillagerStatus, setChannelMemberWithVillagerStatus] = useState([]);
  useEffect(() => {
    const fetchVillagerStatus = async () => {
      const memberStatuses = await Promise.all(
        (channelMember ?? []).map(async (member) => {
          const flag = await isVillager(member.user.id);
          return { ...member, isVillager: flag };
        })
      );
      setChannelMemberWithVillagerStatus(memberStatuses);
    };
    if (channelMember?.length) fetchVillagerStatus();
  }, [channelMember]);

  /* ───────── UI ───────── */
  const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: "#44b700",
      color: "#44b700",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      "&::after": {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        animation: "ripple 1.2s infinite ease-in-out",
        border: "1px solid currentColor",
        content: '""',
      },
    },
    "@keyframes ripple": {
      "0%": { transform: "scale(.8)", opacity: 1 },
      "100%": { transform: "scale(2.4)", opacity: 0 },
    },
  }));

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <MuteComponent />
      </Modal>

      <div className="flex flex-col space-y-2 my-2">
        <Button onClick={handleOpen} variant="outlined" disabled={!channel}>
          {isMute ? "ミュートをやめる" : "このチャンネルをミュート"}
        </Button>
      </div>

      <div className="font-bold text-lg">メンバー</div>
      <div>
        {channelMemberWithVillagerStatus && user?.isAdmin && (
          <>
            {channelMemberWithVillagerStatus.map(
              (member) =>
                member?.isVillager && (
                  <div
                    key={member.user.id}
                    className="flex justify-between space-x-1 items-center rounded-md border w-full my-1 py-2 px-4 shadow-md hover:cursor-pointer hover:shadow-xl duration-150 hover:bg-gray-50 group"
                  >
                    <div
                      className="flex  space-x-2 items-center"
                      onClick={() => window.open("/user/" + member?.user.id, "_blank")}
                    >
                      <div className="flex flex-col justify-center items-center space-y-2 ">
                        {member?.user.online ? (
                          <StyledBadge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            variant="dot"
                          >
                            <Avatar alt={member?.user.name} src={member?.user.image} />
                          </StyledBadge>
                        ) : (
                          <Avatar src={member?.user.image} />
                        )}
                      </div>
                      <div className="">
                        <div className="flex space-x-1 items-center">
                          <div className="font-bold">{member?.user.name}</div>
                          <div className="text-xs text-gray-500">
                            @<GetUserPrefecture userId={member?.user.id} />
                          </div>
                        </div>
                        <div>{new Date(member?.updated_at).toLocaleString()}</div>
                      </div>
                      <div className="group-hover:block hidden duration-150">
                        <Button size="small" variant="outlined">
                          プロフィール
                        </Button>
                      </div>
                    </div>

                    {member?.role === "member" && <div>メンバー</div>}
                    {member?.role === "owner" && <div>オーナー</div>}
                  </div>
                )
            )}
          </>
        )}
      </div>
    </>
  );
};

export default GroupChatSetting;
